import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import PageLayout from '../../../components/base/PageLayout';

const WisdomButlerSkills: React.FC = () => {
  useEffect(() => {
    document.title = '執事スキル - プロ執事の専門技術・時間管理・観察力の磨き方｜執事ベル';
    
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'プロフェッショナル執事が実践する専門スキルを公開。執事の一日の流れ、観察力の磨き方、効率的な時間管理術、サービス技術など、執事ならではの実践的なノウハウを詳しく解説。執事を目指す方はもちろん、ビジネスパーソンの業務効率化、サービス業のスキルアップにも応用できる、プロフェッショナルの技術と心構えをお伝えします。');
    }
  }, []);

  const articles = [
    {
      title: '執事の一日',
      excerpt: 'プロフェッショナルな執事の日常業務と時間管理の技術を詳しくご紹介します。',
      path: '/wisdom/articles/daily-routine',
      readTime: '12分',
      category: '執事の日常',
      image: '/images/generated/seq-daily-routine-card-butler-1-d049ac.jpg'
    },
    {
      title: '観察力の磨き方',
      excerpt: 'お客様のニーズを察知するための観察力と洞察力を養う方法を解説。',
      path: '/wisdom/articles/observation',
      readTime: '9分',
      category: '観察力',
      image: '/images/generated/seq-observation-card-butler-2-e57e02.jpg'
    },
    {
      title: '時間管理の技術',
      excerpt: '効率的で質の高いサービスを提供するための時間管理術をお伝えします。',
      path: '/wisdom/articles/time-management',
      readTime: '8分',
      category: '時間管理',
      image: '/images/generated/seq-time-management-card-butler-3-0124b5.jpg'
    }
  ];

  const skills = [
    {
      category: '基本スキル',
      items: [
        '接客サービスの基本',
        '清掃・整理整頓技術',
        '食事サービス',
        '衣類管理・メンテナンス'
      ]
    },
    {
      category: '専門技術',
      items: [
        'ワインサービス',
        'テーブルセッティング',
        'イベント運営',
        'セキュリティ管理'
      ]
    },
    {
      category: 'コミュニケーション',
      items: [
        '多言語対応',
        '文化的配慮',
        '機密保持',
        '問題解決能力'
      ]
    },
    {
      category: '管理能力',
      items: [
        'スケジュール管理',
        '予算管理',
        'スタッフ指導',
        '品質管理'
      ]
    }
  ];

  return (
    <PageLayout hero={<>
<section 
          className="relative py-20 md:py-32 bg-cover bg-center"
          style={{
            backgroundImage: `linear-gradient(rgba(0,0,0,0.4), rgba(0,0,0,0.4)), url('/images/generated/seq-butler-skills-hero-hands-838192.jpg')`
          }}
        >
          <div className="max-w-7xl mx-auto px-5 sm:px-6 lg:px-8">
            <div className="text-center text-white">
              <h1 className="text-2xl md:text-6xl font-serif mb-4 md:mb-6">
                執事スキル
              </h1>
              <p className="text-sm md:text-2xl max-w-6xl mx-auto leading-relaxed px-5 sm:px-6 lg:px-8">
                プロフェッショナルな執事として必要な<br />
                技術と知識の全てをお伝えします
              </p>
            </div>
          </div>
        </section>
</>}>
      <div className="min-h-screen bg-gradient-to-b from-[#f8f8f8] to-white">
        {/* Hero Section */}
        

        {/* Introduction */}
        <section className="py-12 md:py-20">
          <div className="max-w-6xl mx-auto px-5 sm:px-6 lg:px-8">
            <div className="text-center mb-12 md:mb-16">
              <h2 className="text-xl md:text-4xl font-serif text-gray-900 mb-6 md:mb-8">
                執事という職業
              </h2>
              <div className="prose prose-lg max-w-none text-gray-700 leading-relaxed">
                <p className="text-sm md:text-xl mb-4 md:mb-6">
                  執事は単なる使用人ではありません。お客様の生活を豊かにし、快適な環境を提供するプロフェッショナルです。そのためには幅広い知識と高度な技術、そして何より相手を思いやる心が必要です。
                </p>
                <p className="text-sm md:text-lg mb-4 md:mb-6">
                  執事文化の研究と実践を通じて学んだ技術は、執事としてだけでなく、あらゆるサービス業や日常生活においても活用できる価値あるスキルです。細部への注意、効率的な作業方法、そして相手の立場に立った思考法は、どのような場面でも役立ちます。
                </p>
                <p className="text-sm md:text-lg">
                  ここでは、執事として必要とされる実践的なスキルと知識を、体系的にお伝えしていきます。
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Skills Matrix */}
        <section className="py-12 md:py-20 bg-gray-50">
          <div className="max-w-7xl mx-auto px-5 sm:px-6 lg:px-8">
            <div className="text-center mb-12 md:mb-16">
              <h2 className="text-xl md:text-4xl font-serif text-gray-900 mb-3 md:mb-4">
                執事に必要なスキル
              </h2>
              <p className="text-sm md:text-lg text-gray-600 max-w-2xl mx-auto">
                プロフェッショナルとして身につけるべき技術を分野別にご紹介
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 md:gap-8">
              {skills.map((skillGroup, index) => (
                <div key={index} className="bg-white p-5 md:p-6 rounded-lg shadow-md">
                  <h3 className="text-base md:text-xl font-serif text-gray-900 mb-3 md:mb-4 text-center">
                    {skillGroup.category}
                  </h3>
                  <ul className="space-y-2 md:space-y-3">
                    {skillGroup.items.map((skill, skillIndex) => (
                      <li key={skillIndex} className="flex items-center text-gray-700">
                        <div className="w-2 h-2 bg-[#917c50] rounded-full mr-3 flex-shrink-0"></div>
                        <span className="text-xs md:text-sm">{skill}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Professional Standards */}
        <section className="py-12 md:py-20">
          <div className="max-w-7xl mx-auto px-5 sm:px-6 lg:px-8">
            <div className="text-center mb-12 md:mb-16">
              <h2 className="text-xl md:text-4xl font-serif text-gray-900 mb-3 md:mb-4">
                プロフェッショナルの基準
              </h2>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
              {[
                {
                  icon: 'ri-eye-line',
                  title: '細部への注意',
                  description: '小さな変化や不具合にも気づき、常に最高の状態を維持する観察力と行動力'
                },
                {
                  icon: 'ri-time-line',
                  title: '時間管理',
                  description: '効率的なスケジューリングと優先順位の判断により、質の高いサービスを提供'
                },
                {
                  icon: 'ri-shield-check-line',
                  title: '信頼性',
                  description: '機密保持と約束の履行により、お客様からの絶対的な信頼を獲得'
                },
                {
                  icon: 'ri-lightbulb-line',
                  title: '問題解決',
                  description: '予期せぬ状況にも冷静に対処し、最適な解決策を迅速に見つける能力'
                },
                {
                  icon: 'ri-heart-line',
                  title: '思いやり',
                  description: 'お客様の気持ちに寄り添い、心からのサービスを提供する姿勢'
                },
                {
                  icon: 'ri-graduation-cap-line',
                  title: '継続学習',
                  description: '常に新しい知識と技術を学び、サービスの質を向上させ続ける意欲'
                }
              ].map((standard, index) => (
                <div key={index} className="text-center">
                  <div className="w-12 h-12 md:w-16 md:h-16 flex items-center justify-center mx-auto mb-3 md:mb-4 bg-[#917c50]/10 rounded-full">
                    <i className={`${standard.icon} text-xl md:text-2xl text-[#917c50]`}></i>
                  </div>
                  <h3 className="text-base md:text-xl font-serif text-gray-900 mb-3 md:mb-4">
                    {standard.title}
                  </h3>
                  <p className="text-xs md:text-base text-gray-600 leading-relaxed">
                    {standard.description}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Articles */}
        <section className="py-12 md:py-20 bg-gray-50">
          <div className="max-w-7xl mx-auto px-5 sm:px-6 lg:px-8">
            <div className="text-center mb-12 md:mb-16">
              <h2 className="text-xl md:text-4xl font-serif text-gray-900 mb-3 md:mb-4">
                関連記事
              </h2>
              <p className="text-sm md:text-lg text-gray-600 max-w-2xl mx-auto">
                執事スキルを実践的に学ぶための詳細な記事
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
              {articles.map((article, index) => (
                <Link
                  key={index}
                  to={article.path}
                  className="group bg-white rounded-lg shadow-md overflow-hidden hover:shadow-lg transition-all duration-300 transform hover:-translate-y-1"
                >
                  <div className="relative h-48 bg-gradient-to-br from-amber-100 to-amber-200">
                    <img 
                      src={article.image}
                      alt={article.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                    />
                    <div className="absolute top-4 left-4">
                      <span className="px-3 py-1 bg-white/90 backdrop-blur-sm rounded-full text-xs font-medium text-amber-700">
                        {article.category}
                      </span>
                    </div>
                    <div className="absolute top-4 right-4">
                      <span className="px-3 py-1 bg-black/20 backdrop-blur-sm rounded-full text-xs font-medium text-white">
                        {article.readTime}
                      </span>
                    </div>
                  </div>
                  <div className="p-6">
                    <h3 className="text-base md:text-xl font-serif text-gray-900 mb-3 group-hover:text-[#917c50] transition-colors">
                      {article.title}
                    </h3>
                    <p className="text-xs md:text-sm text-gray-600 leading-relaxed mb-4">
                      {article.excerpt}
                    </p>
                    <div className="flex items-center text-[#917c50] group-hover:text-[#7a6940]">
                      <span className="text-xs md:text-sm font-medium">記事を読む</span>
                      <i className="ri-arrow-right-line ml-2 w-4 h-4 flex items-center justify-center"></i>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-12 md:py-20 bg-gradient-to-r from-[#1a1a1a] to-[#2d2d2d]">
          <div className="max-w-6xl mx-auto px-5 sm:px-6 lg:px-8 text-center">
            <h2 className="text-xl md:text-4xl font-serif text-white mb-4 md:mb-6">
              執事スキルを身につけませんか
            </h2>
            <p className="text-sm md:text-xl text-gray-300 mb-6 md:mb-8 leading-relaxed px-5 sm:px-6 lg:px-8">
              プロフェッショナルな技術を学び、あらゆる場面で活用できるスキルを習得していただけます
            </p>
            <Link
              to="/services"
              className="inline-block bg-[#917c50] text-white px-6 md:px-8 py-3 md:py-4 rounded-lg font-medium hover:bg-[#7a6940] transition-colors whitespace-nowrap text-sm md:text-base"
            >
              研修プログラムを見る
            </Link>
          </div>
        </section>
      </div>
    </PageLayout>
  );
};

export default WisdomButlerSkills;
